/* Diff panel — Sublime-Merge style: per-file expandable cards with inline diff */

import { state, refs, esc, syncBadgeHtml } from './state.js';
import { api } from './api.js';
import { confirm, confirmPullFromCloud, confirmPushToCloud } from './dialogs.js';
import {
  loadSyncStatuses, loadFileTree, renderFileTree,
  pullWithFeedback, pushWithFeedback, bulkSync,
} from './file-tree.js';
import { showPanel, hidePanel, getPanelContainer } from './panel-registry.js';
import { renderWorkspaceTabs } from './search.js';
import { notify } from './toast.js';
import { renderDiffInto } from './diff-render.js';

// `kind` (folder name) → `apiKind` (URL segment used by /api/sync/...).
const KIND_TO_API = {
  "global-scripts": "scripts",
  "custom-calculations": "calculations",
  "ui-templates": "templates",
};

/** @deprecated kept for backward-compat; no-op now that layout is zone-based */
export function animateGrid(fn) {
  if (typeof fn === 'function') fn();
}

function _el(id) { return document.getElementById(id); }

function _ensureDiffPanelContent() {
  const diffPanel = getPanelContainer('diff');
  if (!diffPanel) return;
  diffPanel.innerHTML = `
    <div class="diff-panel-inner">
      <div class="diff-header">
        <h3 class="diff-title">Sync Changes</h3>
        <div class="diff-bulk-actions">
          <button class="btn btn-xs" id="btnPullAll" title="Pull all from cloud">Pull All</button>
          <button class="btn btn-xs" id="btnPushAll" title="Push all to cloud">Push All</button>
          <button class="btn btn-xs" id="btnStashAll" title="Stash all local-only files">Stash All</button>
        </div>
        <button class="btn-icon" id="btnCloseDiff" title="Close">
          <i class="ic ic-x"></i>
        </button>
      </div>
      <div class="diff-file-list" id="diffFileList"></div>
    </div>
  `;

  _el('btnCloseDiff')?.addEventListener('click', () => hideDiffPanel());

  _el('btnPullAll')?.addEventListener('click', async () => {
    const toPull = (state.syncStatuses || []).filter(s => s.direction === 'remote_only' || s.direction === 'conflict');
    await bulkSync('pull', toPull, { reloadTree: true });
    state.diffCache.clear();
    showDiffPanel();
  });

  _el('btnPushAll')?.addEventListener('click', async () => {
    const toPush = (state.syncStatuses || []).filter(s => s.direction === 'local_only' || s.direction === 'conflict');
    await bulkSync('push', toPush);
    state.diffCache.clear();
    showDiffPanel();
  });

  _el('btnStashAll')?.addEventListener('click', async () => {
    // Destructive: discards local-only work — guard it.
    const localOnly = (state.syncStatuses || []).filter(s => s.direction === 'local_only').length;
    const ok = await confirm(
      'Stash all local-only files',
      `Discard ${localOnly || 'all'} local-only file${localOnly === 1 ? '' : 's'}? This removes local work that hasn't been pushed to the cloud and cannot be undone.`,
    );
    if (!ok) return;
    try { await api.syncStashAll(); } catch (e) { console.warn('Stash all failed:', e); }
    await loadSyncStatuses();
    await loadFileTree();
    state.diffCache.clear();
    showDiffPanel();
  });
}

export function showDiffPanel(opts = {}) {
  state.diffPanelOpen = true;
  showPanel('diff');
  if (!_el('diffFileList')) _ensureDiffPanelContent();
  const statuses = state.syncStatuses || [];
  let outOfSync = statuses.filter((s) => s.direction !== "in_sync");
  // Optional single-file scope: keep only the matching item.
  if (opts.filterToName) {
    outOfSync = outOfSync.filter(
      (s) => s.name === opts.filterToName
        && (!opts.filterToKind || s.kind === opts.filterToKind),
    );
  }
  state.diffFiles = outOfSync;
  // Drop expanded entries for files that are no longer in the visible list
  const live = new Set(state.diffFiles.map(f => f.name));
  for (const n of [...state.expandedDiffNames]) {
    if (!live.has(n)) state.expandedDiffNames.delete(n);
  }
  renderDiffFileList();
  renderWorkspaceTabs();
}

export function hideDiffPanel() {
  state.diffPanelOpen = false;
  hidePanel('diff');
  renderWorkspaceTabs();
}

function _diffCacheKey(item) {
  return `${item.kind}/${item.name}`;
}

function _isExpanded(item) {
  return state.expandedDiffNames.has(item.name);
}

function _toggleExpanded(item) {
  if (state.expandedDiffNames.has(item.name)) {
    state.expandedDiffNames.delete(item.name);
  } else {
    state.expandedDiffNames.add(item.name);
  }
}

function renderDiffFileList() {
  const fileList = _el('diffFileList');
  if (!fileList) return;
  fileList.innerHTML = "";
  if (state.diffFiles.length === 0) {
    fileList.innerHTML = `<div class="diff-empty-state">All files in sync</div>`;
    return;
  }

  for (const item of state.diffFiles) {
    fileList.appendChild(_renderDiffCard(item));
  }
}

function _renderDiffCard(item) {
  const expanded = _isExpanded(item);
  const card = document.createElement("div");
  card.className = `diff-file-card ${expanded ? "expanded" : ""}`;
  card.dataset.name = item.name;
  card.dataset.kind = item.kind;

  // ---- Header row -----------------------------------------------------------
  const header = document.createElement("div");
  header.className = "diff-file-card-header";

  const isQuoteTable = item.kind === "quote-tables";
  const kindDisplay = isQuoteTable && item.label
    ? `Quote Table "${esc(item.label)}"`
    : (item.label ? `${item.kind} "${esc(item.label)}"` : item.kind);

  header.innerHTML = `
    <span class="diff-card-caret ${expanded ? "open" : ""}">&#9654;</span>
    ${syncBadgeHtml(item)}
    <span class="diff-file-name">${esc(item.name)}</span>
    <span class="diff-file-dir">${kindDisplay}</span>
    <span class="diff-card-actions"></span>
  `;

  // Action buttons (right-aligned via .diff-card-actions span)
  const actionsEl = header.querySelector(".diff-card-actions");
  const apiKind = KIND_TO_API[item.kind] || item.kind;

  if (item.direction === "local_only" || item.direction === "conflict") {
    actionsEl.appendChild(_makeActionBtn("Push", "btn-primary", async (e) => {
      e.stopPropagation();
      const ok = await confirmPushToCloud(item.name, state.tenant);
      if (!ok) return;
      const success = await pushWithFeedback(apiKind, item.name);
      if (success) {
        state.diffCache.delete(_diffCacheKey(item));
        showDiffPanel();
      }
    }));
  }

  if (item.direction === "remote_only" || item.direction === "conflict") {
    actionsEl.appendChild(_makeActionBtn("Pull", "", async (e) => {
      e.stopPropagation();
      const ok = await confirmPullFromCloud(item.name);
      if (!ok) return;
      const success = await pullWithFeedback(apiKind, item.name);
      if (success) {
        state.diffCache.delete(_diffCacheKey(item));
        showDiffPanel();
      }
    }));
  }

  if (item.direction === "local_only") {
    actionsEl.appendChild(_makeActionBtn("Stash", "", async (e) => {
      e.stopPropagation();
      try {
        await api.syncStash(apiKind, item.name);
        state.diffCache.delete(_diffCacheKey(item));
        notify(`Stashed ${item.name}`, 'success');
        await loadSyncStatuses(true);
        await loadFileTree();
        showDiffPanel();
      } catch (err) {
        notify(`Stash failed: ${err.message}`, 'error');
      }
    }));
  }

  header.addEventListener("click", () => {
    _toggleExpanded(item);
    _renderCardExpansion(card, item);
  });

  card.appendChild(header);

  // ---- Body (lazy) ----------------------------------------------------------
  const body = document.createElement("div");
  body.className = "diff-file-card-body";
  card.appendChild(body);

  if (expanded) _populateCardBody(body, item);

  return card;
}

function _renderCardExpansion(card, item) {
  const expanded = _isExpanded(item);
  card.classList.toggle("expanded", expanded);
  const caret = card.querySelector(".diff-card-caret");
  if (caret) caret.classList.toggle("open", expanded);
  const body = card.querySelector(".diff-file-card-body");
  if (!body) return;
  if (expanded) {
    _populateCardBody(body, item);
  } else {
    body.innerHTML = "";
  }
}

async function _populateCardBody(body, item) {
  const cacheKey = _diffCacheKey(item);
  const cached = state.diffCache.get(cacheKey);
  if (cached) {
    renderDiffInto(body,cached);
    return;
  }
  body.innerHTML = `<div class="diff-empty">Loading diff...</div>`;
  try {
    const apiKind = KIND_TO_API[item.kind] || item.kind;
    const result = await api.syncDiff(apiKind, item.name);
    state.diffCache.set(cacheKey, result);
    renderDiffInto(body,result);
  } catch (e) {
    body.innerHTML = `<div class="diff-empty diff-error">Error: ${esc(e.message)}</div>`;
  }
}

function _makeActionBtn(label, extraClass, onClick) {
  const btn = document.createElement("button");
  btn.className = `btn btn-xs ${extraClass}`.trim();
  btn.textContent = label;
  btn.addEventListener("click", onClick);
  return btn;
}

// Inverse of KIND_TO_API — used to translate a file-tree apiKind into the
// `kind` field on sync status entries.
const API_TO_KIND = Object.fromEntries(
  Object.entries(KIND_TO_API).map(([k, v]) => [v, k]),
);

/**
 * Open the diff panel scoped to a single file and expand its card.
 * Called from file-tree "View diff" — users expect this to show a diff
 * for the right-clicked file, not the global out-of-sync list.
 *
 * If the file is in sync (no diff to show), surface a toast and bail out
 * instead of opening a panel full of unrelated items.
 */
export async function openDiffForScript(scriptName, apiKind = "scripts") {
  const targetKind = API_TO_KIND[apiKind] || null;
  const status = (state.syncStatuses || []).find(
    (s) => s.name === scriptName && (!targetKind || s.kind === targetKind),
  );
  if (!status || status.direction === "in_sync") {
    notify(`${scriptName} is in sync — no changes to show`, 'info');
    return;
  }
  showDiffPanel({ filterToName: scriptName, filterToKind: targetKind });
  state.expandedDiffNames.add(scriptName);
  renderDiffFileList();
  // Scroll the freshly expanded card into view
  const card = document.querySelector(`.diff-file-card[data-name="${CSS.escape(scriptName)}"]`);
  if (card) card.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
}

// ---------------------------------------------------------------------------
// Timeline adapter: display a raw unified diff in the diff panel
// ---------------------------------------------------------------------------

/**
 * Open the diff panel showing a single raw unified diff (from the rewind journal).
 * Called by timeline.js when a user clicks a row to inspect a recorded change.
 *
 * @param {{ title: string, unifiedDiff: string }} opts
 */
export function showRawDiff({ title, unifiedDiff }) {
  state.diffPanelOpen = true;
  showPanel('diff');
  _ensureDiffPanelContent();

  const list = _el('diffFileList');
  if (!list) return;

  // Render a single card directly, bypassing the sync-status path
  list.innerHTML = "";

  const card = document.createElement('div');
  card.className = 'diff-file-card expanded';

  const header = document.createElement('div');
  header.className = 'diff-file-card-header';
  header.innerHTML = `<span class="diff-card-caret open">&#9654;</span>
    <span class="diff-file-name">${esc(title)}</span>`;
  card.appendChild(header);

  const body = document.createElement('div');
  body.className = 'diff-file-card-body';
  // parseUnifiedDiff lives in diff-render.js
  renderDiffInto(body, { diff: unifiedDiff });
  card.appendChild(body);

  list.appendChild(card);
}
